import { useState } from 'react';
import { lt, useDialogAction } from '@mews/framework';
import { WithAccountId } from '@mews-commander/types';
import { TooltippedIconButton } from '@mews-commander/ui';
import { IconName } from '@optimus-web/icons';
import { PaymentPlan, PaymentRequestType } from '@mews-commander/data-access';
import { DropDownButton, DropdownItem, Icon } from '@optimus-web/core';
import { DataTestId } from '@mews-commander/enums';
import { getFilteredProcessPaymentDialogTypes } from '../../forms/ProcessPayment/utils';
import { useProcessPaymentDialog } from '../../forms/ProcessPayment/useProcessPaymentDialog';
import { useCancelPaymentPlan } from './queries';
import { CancelPaymentPlanDialog } from './CancelPaymentPlanDialog';
import { WithPaymentPlanId } from './types';
import { PAYMENT_TYPES_MANUAL_PAYMENT } from './const';
import { getEnabledPaymentMethodId } from './utils';

export const PaymentPlanActions = ({
    paymentPlanId,
    accountId,
    reservationId,
    paymentMethodData,
}: {
    reservationId: PaymentPlan['reservationId'];
    paymentMethodData?: PaymentPlan['paymentMethodData'];
} & WithPaymentPlanId &
    WithAccountId) => {
    const { mutateAsync } = useCancelPaymentPlan({
        accountId,
        paymentMethodId: paymentMethodData?.value?.id,
        paymentMethod: paymentMethodData?.discriminator,
    });
    const cancellationAction = useDialogAction(async () => await mutateAsync({ id: paymentPlanId }));
    const { chargeGuestPaymentTypes, externalPaymentTypes } =
        getFilteredProcessPaymentDialogTypes(PAYMENT_TYPES_MANUAL_PAYMENT);
    const [hasChargeGuestPaymentTypes, setHasChargeGuestPaymentTypes] = useState(true);
    const allowedPaymentTypes = hasChargeGuestPaymentTypes ? chargeGuestPaymentTypes : externalPaymentTypes;
    const dialogTitle = hasChargeGuestPaymentTypes
        ? lt('ProcessPaymentPanelTitleForOptions')
        : lt('ProcessPaymentDialogAddExternalPaymentsTitle');
    const [renderProcessPaymentDialog, toggleProcessPaymentDialog] = useProcessPaymentDialog({
        accountId,
        title: dialogTitle,
        allowedPaymentTypes,
        defaultSelectedFormValues: {
            paymentPlanId,
            serviceOrderId: reservationId,
            paymentMethodId: getEnabledPaymentMethodId(paymentMethodData),
        },
        readOnlyFormFields: { isPaymentPlanIdReadOnly: true },
        allowedPaymentRequestTypeOptions: [PaymentRequestType.PaymentMethod, PaymentRequestType.Payment],
    });

    return (
        <>
            <TooltippedIconButton
                tooltip={lt('PaymentPlansActionTooltipProcessPayment')}
                icon={IconName.Payments}
                size="small"
                variant="ghost"
                onClick={() => {
                    setHasChargeGuestPaymentTypes(true);
                    toggleProcessPaymentDialog();
                }}
                data-test-id={DataTestId.ProcessPaymentButton}
                type="button"
            />
            <DropDownButton icon={IconName.More} size="small" variant="ghost">
                <DropdownItem
                    label={lt('PaymentPlansActionTooltipCancelPlan')}
                    onClick={cancellationAction.handlers.handleOpen}
                    leftDetail={<Icon value={IconName.CancelSquare} />}
                    data-test-id={DataTestId.CancelPaymentPlanButton}
                    key="cancel-payment-plan"
                />
                <DropdownItem
                    label={lt('PaymentPlansActionTooltipProcessExternalPayment')}
                    onClick={() => {
                        setHasChargeGuestPaymentTypes(false);
                        toggleProcessPaymentDialog();
                    }}
                    leftDetail={<Icon value={IconName.ExternalPayment} />}
                    data-test-id={DataTestId.ProcessExternalPaymentButton}
                    key="process-external-payment"
                />
            </DropDownButton>
            {renderProcessPaymentDialog()}
            <CancelPaymentPlanDialog actions={cancellationAction} />
        </>
    );
};
